import { Sentry } from 'lib-shared-sentry';
import { Action, Middleware } from 'redux';

import { AppState } from 'bootstrap.store';

import { syncActions } from './sync.actions';
import { Message } from './sync.types';

const isBroadcastChannelSupported = typeof BroadcastChannel !== 'undefined';

/** The broadcast channel. */
const broadcastChannel = isBroadcastChannelSupported
  ? new BroadcastChannel('dixa-sync')
  : null;

/** The actions that are currently being synchronized. */
const pendingActions = new Set<string>();

/** Represents the sync middleware options. */
type SyncMiddlewareOptions = {
  /**
   * The action predicate to determine whether an action should synchronize
   * across browsing contexts.
   *
   * @param action The action.
   */
  readonly actionPredicate: (action: Action) => boolean;
  /**
   * The state to be shared with the other browsing context.
   *
   * This accepts the state and returns a partial of the state which will then
   * merged with the other browsing context state.
   *
   * @param state The state.
   */
  readonly prepareState: (state: AppState) => Partial<AppState>;
};

/**
 * Creates the sync middleware that handles synchronization of redux state and
 * actions across browsing contexts.
 *
 * @param options The options.
 */
export const createSyncMiddleware: (
  options: SyncMiddlewareOptions,
) => Middleware<unknown, AppState> =
  ({ actionPredicate, prepareState }) =>
  ({ dispatch, getState }) => {
    // Return if broadcastChannel is unsupported
    if (!broadcastChannel) {
      return (next) => (action) => {
        return next(action);
      };
    }
    /**
     * Posts a message through the channel.
     *
     * @param message The message.
     */
    const postMessage = (message: Message) =>
      broadcastChannel.postMessage(message);

    /** Handles receiving a message through the channel. */
    broadcastChannel.onmessage = ({ data: message }: MessageEvent<Message>) => {
      switch (message.type) {
        case 'action':
          pendingActions.add(message.params.action.type);
          dispatch(message.params.action);
          break;
        case 'state:request':
          postMessage({
            type: 'state:response',
            params: { state: prepareState(getState()) },
          });
          break;
        case 'state:response':
          dispatch(syncActions.state({ state: message.params.state }));
          break;
      }
    };

    // Asks the other browsing context(s) for the state to be shared with the
    // current browsing context.
    postMessage({ type: 'state:request' });

    return (next) => (action) => {
      try {
        if (actionPredicate(action)) {
          if (pendingActions.has(action.type)) {
            pendingActions.delete(action.type);
          } else {
            postMessage({ type: 'action', params: { action } });
          }
        }
      } catch (exception) {
        Sentry.captureException(
          new Error('Sync: middleware exception caught'),
          { extra: { exception } },
        );
      }

      return next(action);
    };
  };
